<template>
  <div>
    <base-header type="white">
      <h1 class="text-dark mb-0"><i class="fas fa-camera mr-2"></i>Device Scanner</h1>
    </base-header>

    <b-container fluid class="mt--6 px-3">
      <!-- Floating sync button (always visible) -->
      <div style="position:fixed; bottom:20px; right:20px; z-index:1050;">
        <base-button size="sm" type="primary" @click="showSyncModal = true">
          <i class="fas fa-sync-alt"></i> Sync / Monitor
        </base-button>
      </div>

      <!-- Controls card -->
      <b-card no-body class="card-shadow mb-3">
        <b-card-body class="py-2 px-3">
          <div class="d-flex flex-wrap align-items-end mb-2" style="gap:12px;">
            <div>
              <label class="form-control-label small font-weight-bold text-uppercase text-muted mb-1">Mode</label>
              <div>
                <b-form-radio-group
                  v-model="scanMode"
                  :options="[
                    { text: 'Single', value: 'single' },
                    { text: 'Bulk', value: 'bulk' }
                  ]"
                  buttons button-variant="outline-primary" size="sm"
                />
              </div>
            </div>
            <div v-if="cameras.length > 1" style="min-width:160px;">
              <label class="form-control-label small font-weight-bold text-uppercase text-muted mb-1">Camera</label>
              <b-form-select
                v-model="selectedCameraId" size="sm" :options="cameraOptions"
                @change="switchCamera"
              />
            </div>
            <div class="ml-md-auto">
              <base-button v-if="!scanning" size="sm" type="primary" @click="startCamera">
                <i class="fas fa-play"></i> Start Camera
              </base-button>
              <base-button v-if="scanning" size="sm" type="danger" @click="stopScanner">
                <i class="fas fa-stop"></i> Stop
              </base-button>
            </div>
          </div>
          <!-- Session token display (only when scanning in bulk and session exists) -->
          <div v-if="scanning && sessionToken" class="mt-2 text-center">
            <small class="text-muted">Sync token: <strong>{{ sessionToken }}</strong></small>
            <base-button size="sm" type="outline-info" class="ml-2" @click="copySessionToken">
              <i class="fas fa-copy"></i> Copy
            </base-button>
          </div>
        </b-card-body>
      </b-card>

      <!-- VIEWPORT (only one, uses v-show to keep DOM alive) -->
      <div v-show="scanning" class="scanner-wrapper mb-3">
        <video ref="video" autoplay playsinline class="viewport" :style="{ display: useNativeDetector ? 'block' : 'none' }"></video>
        <div id="scanner-viewport" class="viewport" v-show="!useNativeDetector"></div>
        <div class="scan-overlay">
          <div class="scan-region"></div>
        </div>
        <div v-if="scanMode === 'bulk' && scanning && scannedCodes.length > 0" class="live-count-badge">
          <i class="fas fa-check-circle mr-1"></i> {{ scannedCodes.length }} scanned
        </div>
      </div>

      <!-- ===== MONITOR UI (only when monitoring) ===== -->
      <!-- Monitor card (shown when monitoring) -->
      <b-card v-if="isMonitor" no-body class="card-shadow mb-3">
        <b-card-body class="py-2 px-3">
          <div class="d-flex justify-content-between align-items-center">
            <span>
              <i v-if="monitorStatus === 'scanning'" class="fas fa-circle text-success mr-2"></i>
              <i v-else class="fas fa-stop-circle text-muted mr-2"></i>
              Monitoring — <strong>{{ monitorCodes.length }}</strong> scanned
            </span>
            <b-badge :variant="monitorStatus === 'scanning' ? 'success' : 'secondary'">
              {{ monitorStatus === 'scanning' ? 'Scanning' : 'Stopped' }}
            </b-badge>
          </div>
        </b-card-body>
      </b-card>

      <!-- Monitor scanned list -->
      <b-card v-if="isMonitor && monitorCodes.length > 0" no-body class="card-shadow mb-3">
        <b-card-header><i class="fas fa-list mr-2"></i>Scanned Devices ({{ monitorCodes.length }})</b-card-header>
        <b-card-body class="p-0">
          <b-list-group flush>
            <b-list-group-item v-for="(code, idx) in monitorCodes" :key="idx" class="font-monospace">{{ code }}</b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- BulkAssignManager for monitor session (replaces old DeviceFormModal) -->
      <BulkAssignManager
        v-if="isMonitor && monitorStatus === 'stopped' && monitorCodes.length > 0"
        :macs="monitorCodes"
        @assigned="macs => monitorCodes = monitorCodes.filter(m => !macs.includes(m))"
        @cleared="clearMonitorSession"
      />

      <!-- BulkAssignManager for local bulk scanning (replaces old list + modal) -->
      <BulkAssignManager
        v-if="!isMonitor && scanMode === 'bulk' && scannedCodes.length > 0"
        :macs="scannedCodes"
        @assigned="onBulkAssigned"
        @cleared="scannedCodes = []"
      />

      <!-- Ready message -->
      <b-card v-if="!scanning && !isMonitor && scannedCodes.length === 0 && !showSingleForm" no-body class="card-shadow">
        <b-card-body class="text-center py-5">
          <i class="fas fa-camera fa-5x mb-4" style="color:#11CDEF;"></i>
          <h5>Ready to scan</h5>
          <p class="text-muted">Point the camera at a barcode or label and tap the appropriate action.</p>
        </b-card-body>
      </b-card>

      <!-- SYNC / MONITOR MODAL -->
      <modal :show="showSyncModal" @close="showSyncModal = false">
        <template v-slot:header><h5>Sync / Monitor</h5></template>

        <b-form-group label="Join an existing session:" class="mt-0">
          <b-input-group>
            <b-form-input v-model="joinToken" placeholder="Session token" size="sm" />
            <b-input-group-append>
              <base-button size="sm" type="outline-primary" @click="joinByTokenAndClose">Join</base-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <template v-if="isMonitor">
          <hr>
          <p><i class="fas fa-circle text-success mr-1"></i> Monitoring — {{ monitorCodes.length }} codes</p>
          <base-button size="sm" type="outline-secondary" block @click="clearMonitorSession">Stop Monitoring</base-button>
        </template>
      </modal>

      <!-- SINGLE ADD DEVICE FORM MODAL -->
      <DeviceFormModal
        :show="showSingleForm"
        mode="add"
        :initial-mac="scannedMac"
        @close="showSingleForm = false"
        @saved="onSingleDeviceSaved"
      />
    </b-container>
  </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { mapState, mapActions } from 'vuex'
import { statusOptionsList, commonQualityIssues } from '@/utils/statusHelpers'
import DeviceFormModal from '@/components/DeviceFormModal.vue'
import InventoryPageHeader from '@/components/InventoryPageHeader.vue'
import BulkAssignManager from '@/components/BulkAssignManager.vue'

function backCameraScore(cam) {
  const label = (cam.label || '').toLowerCase()
  const id    = (cam.id   || '').toLowerCase()
  let score = 0
  if (label.includes('back'))        score += 10
  if (label.includes('rear'))        score += 10
  if (label.includes('environment')) score += 10
  if (id.includes('back'))           score += 10
  if (id.includes('environment'))    score += 10
  if (label.includes('front'))       score -= 10
  if (label.includes('selfie'))      score -= 10
  if (label.includes('user'))        score -= 10
  if (id.includes('front'))          score -= 10
  const numId = parseInt(id, 10)
  if (!isNaN(numId)) score += numId
  return score
}

export default {
  name: 'ScanPage',
  components: { InventoryPageHeader, DeviceFormModal, BulkAssignManager },
  data() {
    return {
      scanMode: 'single',
      scanning: false,
      scanner: null,
      scannedCodes: [],
      lastScannedCode: null,
      sameInfo: true,
      showSingleForm: false,
      scannedMac: '',
      cameras: [],
      selectedCameraId: null,
      videoStream: null,
      nativeDetector: null,
      scanFrameTimer: null,
      useNativeDetector: false,
      lastNativeResult: null,
      sessionToken: null,
      // Monitor / sync
      isMonitor: false,
      monitorStatus: 'scanning',
      monitorCodes: [],
      monitorPollTimer: null,
      joinToken: '',
      // Sync modal
      showSyncModal: false,
      // Bulk assign form removed – replaced by BulkAssignManager
    }
  },
  computed: {
    ...mapState('inventory', ['devices', 'companies', 'batches']),
    cameraOptions() {
      return this.cameras.map(cam => {
        const side = backCameraScore(cam) >= 0 ? 'Back' : 'Front'
        return { value: cam.id, text: `${side} – ${cam.label || cam.id}` }
      })
    },
    statusOptions() { return statusOptionsList.map(s => ({ value: s, text: s })) },
    batchOptsList() {
      return this.batches.map(b => ({
        value: `Batch ${b.number} (${b.deliveryDate})`,
        text: `Batch ${b.number} (${b.deliveryDate})`
      }))
    },
    commonQualityIssueOptions() { return commonQualityIssues.map(s => ({ value: s, text: s })) }
  },
  async mounted() {
    this.loadCompanies()
    this.loadBatches()
      // polyfill BarcodeDetector for iOS/Safari using WASM
// In mounted(), before your BarcodeDetector check:
if (typeof BarcodeDetector === 'undefined') {
  await import('barcode-detector/polyfill')
  // polyfill auto-registers window.BarcodeDetector
}

    if (typeof BarcodeDetector !== 'undefined') {
      try {
        this.nativeDetector = new BarcodeDetector({ formats: ['code_128', 'code_39', 'ean_13', 'qr_code'] })
        await this.nativeDetector.detect(document.createElement('canvas'))
        this.useNativeDetector = true
      } catch (e) { this.useNativeDetector = false }
    } else { this.useNativeDetector = false }

    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then(stream => stream.getTracks().forEach(t => t.stop()))
        .catch(() => {})
      const cams = await Html5Qrcode.getCameras()
      this.cameras = cams || []
      if (this.cameras.length) {
        const sorted = [...this.cameras].sort((a, b) => backCameraScore(b) - backCameraScore(a))
        this.selectedCameraId = sorted[0].id
      }
    } catch (e) { console.warn('Camera enumeration failed', e) }
  },
  beforeDestroy() {
    this.stopScanner()
    this.stopNativeScanLoop()
    clearInterval(this.monitorPollTimer)
    if (this.sessionToken) {
      fetch(`/api/scan-sessions/${this.sessionToken}/stop`, { method: 'PUT' }).catch(() => {})
    }
  },
  methods: {
    ...mapActions('inventory', ['addDevice', 'addBatch', 'addCompany', 'loadCompanies', 'loadBatches']),

    // ---- Camera ----
    async startCamera() {
      this.stopNativeScanLoop()
      if (this.scanner) {
        try { await this.scanner.stop(); await this.scanner.clear() } catch (e) {}
        this.scanner = null
      }
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop())
        this.videoStream = null
      }
      this.scanning = false
      this.lastNativeResult = null
      this.lastScannedCode = null

      if (this.useNativeDetector) {
        this.startNativeScanner()
      } else {
        this.startHtml5QrcodeScanner()
      }

      if (this.scanMode === 'bulk' || this.scanMode === 'single') {
        this.createSession().catch(() => {})
      }
    },

    async startNativeScanner() {
      try {
        const video = this.$refs.video
        const constraints = {
          video: {
            deviceId: this.selectedCameraId ? { exact: this.selectedCameraId } : undefined,
            facingMode: !this.selectedCameraId ? 'environment' : undefined,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        }
        this.videoStream = await navigator.mediaDevices.getUserMedia(constraints)
        video.srcObject = this.videoStream
        await video.play()
        this.scanning = true
        this.startNativeScanLoop()
      } catch (err) {
        console.error(err)
        this.scanning = false
        alert('Camera failed: ' + (err?.message || err?.toString()))
      }
    },

    startNativeScanLoop() {
      const video = this.$refs.video
      if (!video || !this.nativeDetector) return
      const tick = async () => {
        if (!this.scanning || !this.videoStream) return
        if (video.readyState >= 2) {
          try {
            const barcodes = await this.nativeDetector.detect(video)
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue
              if (code !== this.lastNativeResult) {
                this.lastNativeResult = code
                this.onBarcodeSuccess(code)
              }
            }
          } catch (e) {}
        }
        this.scanFrameTimer = requestAnimationFrame(tick)
      }
      this.scanFrameTimer = requestAnimationFrame(tick)
    },

    stopNativeScanLoop() {
      if (this.scanFrameTimer) {
        cancelAnimationFrame(this.scanFrameTimer)
        this.scanFrameTimer = null
      }
    },

    async startHtml5QrcodeScanner() {
      try {
        this.scanning = true
        await this.$nextTick()
        this.scanner = new Html5Qrcode('scanner-viewport')
        const config = {
          fps: 15,
          qrbox: { width: 250, height: 100 },
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.ITF, Html5QrcodeSupportedFormats.RSS_14,
            Html5QrcodeSupportedFormats.RSS_EXPANDED, Html5QrcodeSupportedFormats.QR_CODE
          ]
        }
        const cameraConfig = this.selectedCameraId ? { deviceId: { exact: this.selectedCameraId } } : { facingMode: 'environment' }
        await this.scanner.start(cameraConfig, config, this.onBarcodeSuccess, () => {})
      } catch (err) {
        console.error(err)
        this.scanning = false
        alert('Camera failed: ' + (err?.message || err?.toString() || 'Unknown error'))
      }
    },

    async onBarcodeSuccess(decodedText) {
      if (decodedText === this.lastScannedCode) return
      this.lastScannedCode = decodedText
      if (this.scanMode === 'single') {
        if (this.sessionToken) {
          await this.sendCodeToSession(decodedText)
        }
        this.stopScanner()
        const device = this.devices.find(d => d.mac === decodedText)
        if (device) {
          this.$router.push({ name: 'deviceDetail', params: { mac: decodedText } })
        } else {
          this.scannedMac = decodedText
          this.showSingleForm = true
        }
      } else {
        if (!this.scannedCodes.includes(decodedText)) {
          this.scannedCodes.push(decodedText)
          if (this.sessionToken) this.sendCodeToSession(decodedText)
        }
      }
    },

    async sendCodeToSession(mac) {
      if (!this.sessionToken) return
      try {
        await fetch(`/api/scan-sessions/${this.sessionToken}/codes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mac })
        })
      } catch (e) { /* silent */ }
    },

    copySessionToken() {
      if (!this.sessionToken) return
      navigator.clipboard.writeText(this.sessionToken).then(() => alert('Token copied!'))
        .catch(() => alert('Could not copy token: ' + this.sessionToken))
    },

    switchCamera(newDeviceId) {
      if (this.scanning) {
        if (this.useNativeDetector) this.startNativeScanner()
        else this.startHtml5QrcodeScanner()
      }
    },

    async stopScanner() {
      this.stopNativeScanLoop()
      if (this.scanner) {
        try { await this.scanner.stop(); await this.scanner.clear() } catch (e) {}
        this.scanner = null
      }
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop())
        this.videoStream = null
      }
      if (this.sessionToken) {
        await fetch(`/api/scan-sessions/${this.sessionToken}/stop`, { method: 'PUT' }).catch(() => {})
        this.sessionToken = null
      }
      this.scanning = false
      this.lastNativeResult = null
      this.lastScannedCode = null
    },

    removeCode(index) { this.scannedCodes.splice(index, 1) },

    onSingleDeviceSaved() {
      this.showSingleForm = false
      this.$router.push({ name: 'deviceDetail', params: { mac: this.scannedMac } })
    },

    onBulkAssigned(assignedMacs) {
      this.scannedCodes = this.scannedCodes.filter(m => !assignedMacs.includes(m))
    },

    // ---- Sync session helpers ----
    async createSession() {
      try {
        const res = await fetch('/api/scan-sessions', { method: 'POST' })
        const data = await res.json()
          console.log('Session created:', data)  
        this.sessionToken = data.token
      } catch (e) { /* silent */ }
    },

    // ---- Remote session (desktop) ----
    async startRemoteSession() {
      try {
        const res = await fetch('/api/scan-sessions', { method: 'POST' })
        const data = await res.json()
        this.isMonitor = true
        this.monitorStatus = 'scanning'
        this.monitorCodes = []
        this.startMonitorPolling(data.token)
      } catch (e) { alert('Failed to create session') }
    },

    joinByToken() {
      const tok = this.joinToken.trim()
      if (!tok) return
      this.isMonitor = true
      this.monitorStatus = 'scanning'
      this.monitorCodes = []
      this.startMonitorPolling(tok)
      this.joinToken = ''
    },

    startMonitorPolling(token) {
      const fetchCodes = async () => {
        try {
          const res = await fetch(`/api/scan-sessions/${token}`)
          const data = await res.json()
          this.monitorCodes = data.codes || []
          this.monitorStatus = data.status || 'scanning'
          if (data.status === 'stopped' && this.monitorPollTimer) {
            clearInterval(this.monitorPollTimer)
          }
        } catch (e) {}
      }
      if (this.monitorPollTimer) clearInterval(this.monitorPollTimer)
      fetchCodes()
      this.monitorPollTimer = setInterval(fetchCodes, 1000)
    },

    clearMonitorSession() {
      this.isMonitor = false
      this.monitorCodes = []
      this.monitorStatus = 'scanning'
      clearInterval(this.monitorPollTimer)
    },

    // Modal helpers
    startRemoteSessionAndClose() {
      this.startRemoteSession()
      this.showSyncModal = false
    },
    joinByTokenAndClose() {
      this.joinByToken()
      this.showSyncModal = false
    },
  }
}
</script>

<style scoped>
.scanner-wrapper {
  position: relative; width: 100%; max-width: 500px; margin: 0 auto;
  border-radius: 12px; overflow: hidden; flex: 1 1 auto; display: flex; flex-direction: column;
}
.viewport {
  width: 100%; background: #000; object-fit: cover; flex: 1 1 auto;
  min-height: 200px; border-radius: 8px;
}
@media (max-width: 576px) {
  .header { padding-top: 2rem !important; padding-bottom: 0 !important; }
  .header h1 { font-size: 1rem; margin-bottom: 0; }
  .header .container-fluid { padding-left: 0.5rem; padding-right: 0.5rem; }
  .card-body { padding: 0.4rem 0.5rem !important; }
  .form-control-label { font-size: 0.6rem; margin-bottom: 1px; text-transform: none; font-weight: 400; }
  .btn-sm, .b-form-radio-group .btn { font-size: 0.6rem; padding: 0.15rem 0.4rem; line-height: 1.2; }
  .b-form-select, .form-control-sm { font-size: 0.6rem; padding: 0.15rem 0.3rem; height: auto; }
  .d-flex.flex-wrap { gap: 6px !important; }
  .viewport { min-height: 280px; max-height: none; border-radius: 4px; }
  .live-count-badge { top: 4px; right: 4px; padding: 0.1rem 0.4rem; font-size: 0.65rem; }
}
.scan-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none; display: flex; align-items: center; justify-content: center;
}
.scan-region {
  width: 80%; height: 25%; border: 3px solid #11CDEF;
  border-radius: 8px; box-shadow: 0 0 0 9999px rgba(0,0,0,0.5);
}
.live-count-badge {
  position: absolute; top: 12px; right: 12px;
  background: rgba(45, 206, 137, 0.9); color: #fff;
  padding: 0.4rem 0.9rem; border-radius: 30px;
  font-weight: 600; font-size: 0.85rem; z-index: 10;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.font-monospace { font-family: monospace; }
</style>